import { Card, Tag, Tooltip, Space, Empty } from "antd";

// Thanh trạng thái các nguồn dữ liệu ngoài (App Store / Play / Firebase).
//
// VÌ SAO CẦN: rủi ro lớn nhất của mọi connector là HỎNG IM LẶNG — token hết hạn, mất
// quyền bucket, cron chết. Nhìn biểu đồ thì một nguồn chết trông HỆT như "mấy hôm nay
// ít lượt tải". Thanh này là chỗ duy nhất phân biệt được ba trạng thái đó.
//
// ⚠️ Cảnh báo dựa trên `last_success_date` (NGÀY DỮ LIỆU mới nhất), KHÔNG dựa
// `last_run_at` (lần chạy cron). Hai cái khác nhau: store trễ 1–3 ngày nên cron chạy
// thành công hôm nay vẫn chỉ lấy được dữ liệu 2 ngày trước. Bám last_run_at sẽ báo
// "ổn" trong khi dữ liệu đã đứng yên cả tuần.

// Dữ liệu store trễ 1–3 ngày là BÌNH THƯỜNG. Quá mốc này mới coi là có vấn đề.
const STALE_DAYS = 3;

const SOURCE_LABELS = {
    appstore: "App Store (iOS)",
    play: "Google Play (Android)",
    firebase_ios: "Firebase iOS",
    firebase_android: "Firebase Android",
};

// Quy chiếu ngày của từng nguồn — phải hiện ra, nếu không người đọc sẽ tưởng mọi cột
// cùng một ngày (xem ghi chú đầu models/ExternalAppStat.js).
const SOURCE_TIMEZONES = {
    appstore: "ngày UTC",
    play: "ngày giờ Thái Bình Dương",
    firebase_ios: "ngày UTC",
    firebase_android: "ngày UTC",
};

/** Số ngày từ 'YYYY-MM-DD' tới hôm nay. Trả null nếu không có ngày. */
function daysSince(dateStr) {
    if (!dateStr) return null;
    const then = Date.parse(`${dateStr}T00:00:00Z`);
    if (Number.isNaN(then)) return null;
    return Math.floor((Date.now() - then) / 86400000);
}

/**
 * Ba trạng thái, cố ý phân biệt rõ — "chưa cấu hình" KHÔNG phải lỗi, hiện xám.
 * Để nó đỏ thì ngày nào cũng đỏ và chẳng ai còn nhìn cảnh báo nữa.
 */
function statusOf(state) {
    if (!state || !state.last_run_at) {
        return { color: "default", text: "Chưa kết nối", detail: "Chưa có credential — job bỏ qua nguồn này." };
    }
    if (state.consecutive_failures >= 3) {
        return { color: "error", text: `Lỗi ${state.consecutive_failures} lần liên tiếp`, detail: state.last_error };
    }
    if (state.consecutive_failures > 0) {
        return { color: "warning", text: "Lỗi lượt gần nhất", detail: state.last_error };
    }

    const age = daysSince(state.last_success_date);
    if (age === null) {
        return { color: "warning", text: "Chạy OK nhưng chưa có dữ liệu", detail: "Nguồn chưa trả về ngày nào." };
    }
    if (age > STALE_DAYS) {
        return {
            color: "error",
            text: `Dữ liệu cũ ${age} ngày`,
            detail: `Mới nhất: ${state.last_success_date}. Trễ 1–3 ngày là bình thường, quá ${STALE_DAYS} là có vấn đề.`,
        };
    }
    return { color: "success", text: `Mới nhất ${state.last_success_date}`, detail: null };
}

function ExternalSyncStatusBar({ syncState = [], loading }) {
    const known = Object.keys(SOURCE_LABELS);
    const bySource = new Map((syncState || []).map((s) => [s.source, s]));

    if (!loading && known.length === 0) {
        return <Empty description="Chưa khai báo nguồn nào" />;
    }

    return (
        <Card size="small" title="Trạng thái kết nối nguồn ngoài" loading={loading}>
            <Space wrap size={[8, 8]}>
                {known.map((source) => {
                    const st = statusOf(bySource.get(source));
                    const tz = SOURCE_TIMEZONES[source];
                    return (
                        <Tooltip
                            key={source}
                            title={
                                <span>
                                    {st.detail || "Đang chạy bình thường."}
                                    <br />
                                    <i>Mốc ngày của nguồn này: {tz}.</i>
                                </span>
                            }
                        >
                            <Tag color={st.color} style={{ marginInlineEnd: 0 }}>
                                {SOURCE_LABELS[source]} — {st.text}
                            </Tag>
                        </Tooltip>
                    );
                })}
            </Space>
        </Card>
    );
}

export { statusOf, daysSince, STALE_DAYS, SOURCE_LABELS };
export default ExternalSyncStatusBar;
